import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import AuthShell from '@/components/auth/AuthShell';
import { apiFetch, unwrapOne } from '@/api/apiFetch';
import { toastApiError } from '@/lib/query-client';
import { useAuth } from '@/lib/AuthContext';
import { LEGAL_URLS, LEGAL_VERSION } from '@/lib/legal';

/**
 * Pantalla bloqueante de aceptación de términos (RGPD 7.1).
 *
 * Decide con `user.terms_required`, que calcula el SERVIDOR en `/api/auth/me`
 * (flag `TERMS_GATE_ENABLED` + versión aceptada distinta de la vigente). El ERP
 * no tiene flag propio: con el flag apagado el campo llega a false y esto no pinta
 * nada, así que desplegar este componente no cambia el comportamiento.
 *
 * Dos textos, porque son dos situaciones distintas para quien las lee: quien ya
 * aceptó una versión anterior («hemos actualizado») y quien no tiene ninguna
 * aceptación registrada, típicamente un alta por Google (`oauth-callback` no la
 * guarda a propósito).
 *
 * El 409 `LEGAL_VERSION_MISMATCH` significa que este bundle trae una
 * `LEGAL_VERSION` anterior a la del backend: el texto que se enseñó ya no es el
 * vigente. Se pide recargar con un botón y no con un reload automático: si la
 * caché sigue sirviendo el bundle viejo, recargar solo entraría en bucle.
 */
export default function TermsGate({ children }) {
  const { t } = useTranslation();
  const { user, markTermsAccepted, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staleVersion, setStaleVersion] = useState(false);

  if (!user?.terms_required) return children;

  const hadPrevious = Boolean(user.terms_accepted_at);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted || saving) return;
    setSaving(true);
    try {
      const res = await apiFetch('/api/auth/me/accept-terms', {
        method: 'POST',
        body: JSON.stringify({ termsVersion: LEGAL_VERSION }),
      });
      markTermsAccepted(unwrapOne(res) ?? {});
    } catch (error) {
      if (error?.code === 'LEGAL_VERSION_MISMATCH') {
        setStaleVersion(true);
      } else {
        toastApiError(error, t('terms_gate.save_error'));
      }
      setSaving(false);
    }
  };

  const linkStyle = { color: 'hsl(var(--primary))', fontWeight: 500 };

  return (
    <AuthShell>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="terms-gate">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px', color: 'hsl(var(--foreground))' }}>
            {hadPrevious ? t('terms_gate.title_updated') : t('terms_gate.title_missing')}
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0, color: 'hsl(var(--muted-foreground))' }}>
            {hadPrevious ? t('terms_gate.body_updated') : t('terms_gate.body_missing')}
          </p>
        </div>

        {/* Misma casilla y misma clave i18n que el alta (`Register.jsx`): lo que se
            acepta aquí es exactamente lo que acepta un usuario nuevo. */}
        <label
          style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 13, lineHeight: 1.45, color: 'hsl(var(--muted-foreground))', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            name="termsAccepted"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            required
            style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0, accentColor: 'hsl(var(--primary))', cursor: 'pointer' }}
          />
          <span>
            <Trans
              i18nKey="register.terms_checkbox"
              defaults="He leído y acepto los <1>Términos y condiciones</1> y la <3>Política de privacidad</3>."
              components={{
                1: <a href={LEGAL_URLS.terms} target="_blank" rel="noreferrer" style={linkStyle} />,
                3: <a href={LEGAL_URLS.privacy} target="_blank" rel="noreferrer" style={linkStyle} />,
              }}
            />
          </span>
        </label>

        {staleVersion && (
          <div
            role="alert"
            style={{ background: 'hsl(var(--destructive) / 0.1)', border: '1px solid hsl(var(--destructive) / 0.3)', borderRadius: 10, padding: '12px 16px', color: 'hsl(var(--destructive))', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
          >
            <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{t('terms_gate.stale_version')}</span>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{ background: 'none', border: '1px solid currentColor', borderRadius: 8, padding: '4px 10px', color: 'inherit', fontSize: 13, cursor: 'pointer' }}
            >
              {t('terms_gate.reload')}
            </button>
          </div>
        )}

        <button
          type="submit"
          disabled={saving || !accepted || staleVersion}
          className="vx-btn-primary"
          style={{ width: '100%', height: 46, borderRadius: 12, border: 'none', color: 'hsl(var(--primary-foreground))', fontSize: 15, fontWeight: 600, cursor: saving ? 'wait' : (accepted ? 'pointer' : 'not-allowed'), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: (saving || !accepted || staleVersion) ? 0.7 : 1 }}
        >
          {saving ? (
            <><Loader2 style={{ width: 18, height: 18, animation: 'spin 1s linear infinite' }} /> {t('terms_gate.saving')}</>
          ) : (
            <>{t('terms_gate.submit')} <ArrowRight style={{ width: 16, height: 16 }} /></>
          )}
        </button>

        {/* Sin salida, una pantalla que bloquea toda la app deja al usuario atrapado
            si no quiere aceptar. */}
        <button
          type="button"
          onClick={() => logout()}
          style={{ background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
        >
          {t('terms_gate.logout')}
        </button>
      </form>
    </AuthShell>
  );
}
